import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBirthdayCake,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaCloudUploadAlt,
  FaImage,
  FaMagic,
  FaPause,
  FaPlay,
  FaPlus,
  FaTrash,
  FaTv,
  FaVideo,
} from "react-icons/fa";
import ConfirmModal from "../../components/ConfirmModal";
import { uploadTvMedia } from "../../services/tv-disply/tvMediaService";
import {
  CELEBRATION_STATES,
  CELEBRATION_TYPES,
  attachCelebrationMedia,
  createCelebration,
  deleteCelebration,
  pauseCelebration,
  playCelebrationNow,
  publishCelebration,
  subscribeToCelebrations,
} from "../../services/tv-disply/tvCelebrationService";

const TYPE_OPTIONS = [
  { value: CELEBRATION_TYPES.TEXT, label: "Text only", icon: <FaMagic /> },
  { value: CELEBRATION_TYPES.IMAGE, label: "Photo", icon: <FaImage /> },
  { value: CELEBRATION_TYPES.VIDEO, label: "Video", icon: <FaVideo /> },
];

export default function TvCelebrations({ deviceRows = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [creating, setCreating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  const fileRef = useRef(null);
  const [form, setForm] = useState(() => initialForm());

  useEffect(() => subscribeToCelebrations(
    (nextRows) => { setRows(nextRows); setLoading(false); setError(""); },
    (nextError) => { setLoading(false); setError(nextError?.message || "Unable to sync celebrations."); }
  ), []);

  useEffect(() => {
    if (!form.file) { setLocalPreviewUrl(""); return undefined; }
    const url = URL.createObjectURL(form.file);
    setLocalPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.file]);

  const eligibleDevices = useMemo(
    () => deviceRows.filter((device) => device.enabled !== false && device.pairingStatus === "PAIRED"),
    [deviceRows]
  );

  function updateForm(key, value) { setForm((current) => ({ ...current, [key]: value })); }

  async function handleCreate(event) {
    event.preventDefault();
    setCreating(true);
    setError("");
    setFeedback("");
    try {
      const uploadFile = form.type === CELEBRATION_TYPES.TEXT
        ? await renderTextCelebrationFile(form)
        : form.file;
      if (!uploadFile) throw new Error(`Choose a ${form.type === CELEBRATION_TYPES.IMAGE ? "photo" : "video"}.`);
      const upload = uploadTvMedia({ file: uploadFile, onUpdate: (update) => setUploadProgress(update.progress || 0) });
      const result = await upload.promise;
      const media = {
        mediaId: result.id,
        mediaUrl: result.downloadURL,
        storagePath: result.storagePath,
        mimeType: result.metadata?.mimeType || uploadFile.type,
      };
      await createCelebration({
        ...form,
        ...media,
        startsAtMs: new Date(form.startsAt).getTime(),
        endsAtMs: new Date(form.endsAt).getTime(),
      });
      setForm(initialForm());
      if (fileRef.current) fileRef.current.value = "";
      setUploadProgress(0);
      setFeedback("Celebration created. Publish its schedule or choose Play Now.");
    } catch (createError) {
      setError(createError?.message || "Unable to create celebration.");
    } finally {
      setCreating(false);
    }
  }

  async function confirmAction() {
    if (!pendingAction) return;
    setActionBusy(true);
    setError("");
    setFeedback("");
    try {
      const { kind, celebration } = pendingAction;
      let readyCelebration = celebration;
      if ((kind === "publish" || kind === "play") && !celebration.mediaId && celebration.type === CELEBRATION_TYPES.TEXT) {
        const textFile = await renderTextCelebrationFile(celebration);
        const upload = uploadTvMedia({ file: textFile, onUpdate: (update) => setUploadProgress(update.progress || 0) });
        const mediaResult = await upload.promise;
        const media = {
          mediaId: mediaResult.id,
          mediaUrl: mediaResult.downloadURL,
          storagePath: mediaResult.storagePath,
          mimeType: mediaResult.metadata?.mimeType || textFile.type,
        };
        await attachCelebrationMedia(celebration.id, media);
        readyCelebration = { ...celebration, ...media };
      }
      if (kind === "publish") {
        const result = await publishCelebration(readyCelebration, eligibleDevices);
        setFeedback(`Schedule published to ${result.targetCount} TV(s). The existing TV listener will switch at the selected time.`);
      } else if (kind === "play") {
        const result = await playCelebrationNow(readyCelebration, eligibleDevices);
        setFeedback(`Playing “${celebration.title}” now on ${result.targetCount} TV(s).`);
      } else if (kind === "pause") {
        await pauseCelebration(celebration, eligibleDevices);
        setFeedback(`Celebration paused. The previous normal playlist pointer is restored now.`);
      } else if (kind === "delete") {
        await deleteCelebration(celebration);
        setFeedback("Celebration deleted.");
      }
      setPendingAction(null);
    } catch (actionError) {
      setError(actionError?.message || "Celebration action failed.");
    } finally {
      setActionBusy(false);
    }
  }

  return (
    <section className="tv-celebrations-page">
      <div className="tv-celebration-banner">
        <div className="tv-celebration-banner-icon"><FaBirthdayCake /></div>
        <div><span>Priority broadcast studio</span><h2>Make every occasion unforgettable</h2><p>Photo, video or beautiful text—scheduled precisely or played across every TV right now.</p></div>
        <div className="tv-celebration-live-stat"><strong>{eligibleDevices.length}</strong><span><FaTv /> paired TVs</span></div>
      </div>

      {feedback ? <div className="tv-display-feedback is-success"><FaCheckCircle /> {feedback}</div> : null}
      {error ? <div className="tv-display-feedback is-error">{error}</div> : null}

      <div className="tv-celebration-layout">
        <form className="tv-celebration-composer" onSubmit={handleCreate}>
          <div className="tv-celebration-section-head"><div><span>CREATE</span><h3>New celebration</h3></div><FaPlus /></div>

          <label className="tv-outlet-editor-field"><span>Internal name</span><input required maxLength={100} value={form.title} placeholder="Diwali evening celebration" onChange={(event) => updateForm("title", event.target.value)} /></label>

          <div className="tv-celebration-type-grid">
            {TYPE_OPTIONS.map((option) => <button key={option.value} type="button" className={form.type === option.value ? "is-active" : ""} onClick={() => updateForm("type", option.value)}>{option.icon}<span>{option.label}</span></button>)}
          </div>

          {form.type !== CELEBRATION_TYPES.TEXT ? (
            <label className="tv-celebration-upload">
              <input ref={fileRef} type="file" accept={form.type === CELEBRATION_TYPES.IMAGE ? "image/jpeg,image/png,image/webp" : "video/mp4"} onChange={(event) => updateForm("file", event.target.files?.[0] || null)} />
              {form.type === CELEBRATION_TYPES.IMAGE ? <FaImage /> : <FaVideo />}
              <strong>{form.file?.name || `Upload celebration ${form.type.toLowerCase()}`}</strong>
              <small>Click to choose {form.type === CELEBRATION_TYPES.IMAGE ? "JPG, PNG or WEBP" : "an MP4 video"}</small>
            </label>
          ) : null}

          <label className="tv-outlet-editor-field"><span>Title shown on TV</span><input required maxLength={120} value={form.headline} placeholder="Happy Diwali" onChange={(event) => updateForm("headline", event.target.value)} /></label>
          <label className="tv-outlet-editor-field"><span>Message</span><textarea maxLength={300} value={form.message} placeholder="Wishing you joy, light and prosperity" onChange={(event) => updateForm("message", event.target.value)} /></label>

          <div className="tv-celebration-colours">
            <label><span>Background</span><input type="color" value={form.backgroundColor} onChange={(event) => updateForm("backgroundColor", event.target.value)} /></label>
            <label><span>Text</span><input type="color" value={form.textColor} onChange={(event) => updateForm("textColor", event.target.value)} /></label>
            <label><span>Accent</span><input type="color" value={form.accentColor} onChange={(event) => updateForm("accentColor", event.target.value)} /></label>
          </div>

          <div className="tv-celebration-time-grid">
            <label><span><FaCalendarAlt /> From date & time</span><input type="datetime-local" required value={form.startsAt} onChange={(event) => updateForm("startsAt", event.target.value)} /></label>
            <label><span><FaClock /> To date & time</span><input type="datetime-local" required value={form.endsAt} onChange={(event) => updateForm("endsAt", event.target.value)} /></label>
          </div>

          <button className="tv-celebration-create-btn" type="submit" disabled={creating}><FaCloudUploadAlt /> {creating ? `Creating${uploadProgress ? ` · ${uploadProgress}%` : "..."}` : "Create celebration"}</button>
        </form>

        <div className="tv-celebration-preview-column">
          <div className="tv-celebration-section-head"><div><span>LIVE PREVIEW</span><h3>Full-screen TV takeover</h3></div><FaTv /></div>
          <CelebrationPreview celebration={{ ...form, mediaUrl: localPreviewUrl }} />
          <div className="tv-celebration-priority-note"><FaMagic /><div><strong>Priority playback</strong><p>When active, this screen hides the normal playlist completely. Pausing or reaching the end time restores the normal playlist immediately.</p></div></div>
        </div>
      </div>

      <div className="tv-celebration-library-head"><div><span>CELEBRATION LIBRARY</span><h3>Saved moments</h3></div><strong>{rows.length} total</strong></div>
      {loading ? <div className="tv-display-feedback is-loading">Loading celebrations.</div> : null}
      {!loading && !rows.length ? <div className="tv-celebration-empty">No celebration created yet.</div> : null}
      <div className="tv-celebration-card-grid">
        {rows.map((celebration) => (
          <article className={`tv-celebration-card state-${celebration.state.toLowerCase()}`} key={celebration.id}>
            <CelebrationPreview celebration={celebration} compact />
            <div className="tv-celebration-card-body">
              <div className="tv-celebration-card-title"><div><span>{celebration.type}</span><h4>{celebration.title}</h4></div><strong>{celebration.state}</strong></div>
              <p><FaCalendarAlt /> {formatWindow(celebration.startsAtMs, celebration.endsAtMs)}</p>
              <p><FaTv /> {celebration.targetCount || 0} targeted TVs</p>
              <div className="tv-celebration-card-actions">
                <button type="button" onClick={() => setPendingAction({ kind: "publish", celebration })}><FaCalendarAlt /> Publish schedule</button>
                {celebration.state === CELEBRATION_STATES.ACTIVE ? (
                  <button type="button" className="is-pause" onClick={() => setPendingAction({ kind: "pause", celebration })}><FaPause /> Pause now</button>
                ) : (
                  <button type="button" className="is-play" onClick={() => setPendingAction({ kind: "play", celebration })}><FaPlay /> Play now</button>
                )}
                <button type="button" className="is-delete" aria-label={`Delete ${celebration.title}`} onClick={() => setPendingAction({ kind: "delete", celebration })}><FaTrash /></button>
              </div>
            </div>
          </article>
        ))}
      </div>

      <ConfirmModal
        open={Boolean(pendingAction)}
        title={actionTitle(pendingAction)}
        message={actionMessage(pendingAction, eligibleDevices.length)}
        confirmText={actionConfirmText(pendingAction)}
        loading={actionBusy}
        error={error}
        onCancel={() => !actionBusy && setPendingAction(null)}
        onConfirm={confirmAction}
      />
    </section>
  );
}

function CelebrationPreview({ celebration, compact = false }) {
  const style = { backgroundColor: celebration.backgroundColor || "#170A10", color: celebration.textColor || "#FFFFFF", "--celebration-accent": celebration.accentColor || "#FF4F12" };
  return (
    <div className={`tv-celebration-preview${compact ? " is-compact" : ""}`} style={style}>
      {celebration.type === CELEBRATION_TYPES.IMAGE && celebration.mediaUrl ? <img src={celebration.mediaUrl} alt="" /> : null}
      {celebration.type === CELEBRATION_TYPES.VIDEO && celebration.mediaUrl ? <video src={celebration.mediaUrl} muted autoPlay loop playsInline /> : null}
      <div className="tv-celebration-preview-shade" />
      <div className="tv-celebration-preview-copy"><span>APNAARTIST CELEBRATES</span><h3>{celebration.headline || "Your celebration title"}</h3><p>{celebration.message || "A beautiful message for every screen."}</p><i /></div>
    </div>
  );
}

function initialForm() {
  const start = new Date(Date.now() + 5 * 60000);
  const end = new Date(start.getTime() + 60 * 60000);
  return { title: "", type: CELEBRATION_TYPES.TEXT, headline: "", message: "", file: null, startsAt: toLocalInput(start), endsAt: toLocalInput(end), backgroundColor: "#170A10", textColor: "#FFFFFF", accentColor: "#FF4F12" };
}
function toLocalInput(date) { const offset = date.getTimezoneOffset() * 60000; return new Date(date.getTime() - offset).toISOString().slice(0, 16); }
async function renderTextCelebrationFile(form) {
  const canvas = document.createElement("canvas");
  canvas.width = 1920;
  canvas.height = 1080;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Unable to prepare the text celebration image.");
  const gradient = context.createRadialGradient(1450, 120, 40, 960, 540, 1500);
  gradient.addColorStop(0, form.accentColor || "#FF4F12");
  gradient.addColorStop(0.38, form.backgroundColor || "#170A10");
  gradient.addColorStop(1, "#080407");
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.textAlign = "center";
  context.fillStyle = form.accentColor || "#FF4F12";
  context.font = "800 28px Arial, sans-serif";
  context.fillText("APNAARTIST CELEBRATES", 960, 330);
  context.fillStyle = form.textColor || "#FFFFFF";
  context.font = "900 112px Arial, sans-serif";
  drawWrappedText(context, form.headline || "Celebration", 960, 500, 1500, 125);
  context.globalAlpha = 0.9;
  context.font = "500 42px Arial, sans-serif";
  drawWrappedText(context, form.message || "", 960, 730, 1350, 58);
  context.globalAlpha = 1;
  context.fillStyle = form.accentColor || "#FF4F12";
  context.fillRect(900, 875, 120, 8);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
  if (!blob) throw new Error("Unable to prepare the text celebration image.");
  return new File([blob], `celebration_${Date.now()}.png`, { type: "image/png" });
}
function drawWrappedText(context, text, centerX, startY, maxWidth, lineHeight) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width > maxWidth && line) { lines.push(line); line = word; } else { line = candidate; }
  });
  if (line) lines.push(line);
  lines.slice(0, 4).forEach((value, index) => context.fillText(value, centerX, startY + (index * lineHeight)));
}
function formatWindow(start, end) { if (!start || !end) return "Schedule not set"; const format = { dateStyle: "medium", timeStyle: "short" }; return `${new Date(start).toLocaleString([], format)} → ${new Date(end).toLocaleString([], format)}`; }
function actionTitle(action) { if (!action) return "Confirm"; if (action.kind === "play") return "Play over every TV now?"; if (action.kind === "pause") return "Pause celebration now?"; if (action.kind === "delete") return "Delete celebration?"; return "Publish celebration schedule?"; }
function actionConfirmText(action) { if (!action) return "Confirm"; if (action.kind === "play") return "Play now"; if (action.kind === "pause") return "Pause now"; if (action.kind === "delete") return "Delete"; return "Publish schedule"; }
function actionMessage(action, count) { if (!action) return ""; if (action.kind === "play") return `“${action.celebration.title}” will immediately hide the normal playlist on ${count} paired TV(s).`; if (action.kind === "pause") return `The celebration will stop and the normal playlist will resume on ${count} TV(s).`; if (action.kind === "delete") return `“${action.celebration.title}” will be permanently removed.`; return `The selected date and time will be sent to ${count} paired TV(s). TVs activate it automatically during that window.`; }
